import type { EditorApi } from "@circle-react-uikit/TipTapBlockEditor/types";
import type { RefObject } from "react";
import { useEffect } from "react";

import { useCommentsScroll } from "@/react/components/CommentsCompass/Provider/ScrollContext";
import type { TiptapBody } from "@/react/types/RichTextField";

type ComposerInitialContent = Pick<TiptapBody, "body" | "sgids_to_object_map">;

interface UseComposerActivationParams {
  editorApiRef: RefObject<EditorApi | null>;
  initialContent?: ComposerInitialContent | null;
  isFocusPending: boolean;
  onFocusHandled: () => void;
  wrapperRef: RefObject<HTMLDivElement | null>;
}

export const useComposerActivation = ({
  editorApiRef,
  wrapperRef,
  initialContent,
  isFocusPending,
  onFocusHandled,
}: UseComposerActivationParams) => {
  const { scrollToComposer } = useCommentsScroll();

  useEffect(() => {
    if (!isFocusPending) return;
    // Defer past Tiptap's editor-creation effect so the instance is live
    const timer = setTimeout(() => {
      const editorApi = editorApiRef.current;
      if (!editorApi) return;
      if (initialContent && editorApi.isEmpty()) {
        editorApi.setContent(initialContent.body, {
          sgidToObjectMap: initialContent.sgids_to_object_map,
        });
      }
      editorApi.focus();
      scrollToComposer(wrapperRef.current);
      onFocusHandled();
    }, 0);
    return () => clearTimeout(timer);
  }, [
    wrapperRef,
    editorApiRef,
    initialContent,
    isFocusPending,
    onFocusHandled,
    scrollToComposer,
  ]);
};
